import React, { useState, useEffect } from 'react';
import { plansAPI } from '../../services/api';
import Card from '../common/Card';
import Loading from '../common/Loading';
import Button from '../common/Button';
import PlanFormModal from './PlanFormModal';
import { formatDate, handleApiError, showToast, showConfirm } from '../../utils/helpers';

const PlansListPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await plansAPI.getAll();
      setPlans(response.data.plans || []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPlan(null);
    setShowModal(true);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm(
      'Are you sure you want to delete this plan? This action cannot be undone.',
      false
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(id);
      await plansAPI.delete(id);
      await fetchPlans();
      showToast.success('Plan deleted successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setDeletingId(null);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingPlan(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    fetchPlans();
  };

  const formatPrice = (price) => {
    if (price === 0) return 'Free';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const formatLimit = (value) => {
    if (value === null || value === undefined) return 'Unlimited';
    return value.toLocaleString();
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={fetchPlans} variant="secondary">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Subscription Plans</h1>
          <p className="text-gray-600 mt-1">Manage subscription plans for agencies</p>
        </div>
        <Button variant="primary" onClick={handleCreate}>
          Add New Plan
        </Button>
      </div>

      {/* Plans Grid */}
      {plans.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <p className="text-gray-500 text-lg mb-4">No plans yet</p>
            <Button variant="primary" onClick={handleCreate}>
              Add Your First Plan
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card key={plan.id} className="p-6 relative">
              {/* Status Badge */}
              {plan.is_active === false && (
                <div className="absolute top-4 right-4">
                  <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                    Inactive
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                {plan.name_ar && (
                  <p className="text-sm text-gray-600 mb-2">{plan.name_ar}</p>
                )}
                <div className="text-3xl font-bold text-primary-600">
                  {formatPrice(plan.price)}
                  <span className="text-sm font-normal text-gray-500">
                    /{plan.billing_period || 'month'}
                  </span>
                </div>
              </div>

              {/* Description */}
              {plan.description && (
                <p className="text-gray-600 mb-4 text-sm">{plan.description}</p>
              )}

              {/* Limits */}
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Max Posts:</span>
                  <span className="font-medium">{formatLimit(plan.max_posts)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Max Users:</span>
                  <span className="font-medium">{formatLimit(plan.max_users)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Max Clients:</span>
                  <span className="font-medium">{formatLimit(plan.max_clients)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Display Order:</span>
                  <span className="font-medium">{plan.display_order || 0}</span>
                </div>
              </div>

              {/* Features */}
              {plan.features && Array.isArray(plan.features) && plan.features.length > 0 && (
                <div className="mb-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Features:</h4>
                  <div className="space-y-1">
                    {plan.features.map((feature) => (
                      <div key={feature.id} className="flex items-center text-sm">
                        {feature.icon && <span className="text-green-600 mr-2">{feature.icon}</span>}
                        <span className="text-gray-900">{feature.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-4">
                <span className="text-xs text-gray-500">
                  Created: {formatDate(plan.created_at)}
                </span>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={() => handleEdit(plan)}>
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(plan.id)}
                    disabled={deletingId === plan.id}
                  >
                    {deletingId === plan.id ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Plan Form Modal */}
      {showModal && (
        <PlanFormModal
          plan={editingPlan}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default PlansListPage;







